import { errorToast } from "@/shared/lib/apiErrors";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
  Package,
  Pencil,
  Sparkles,
} from "lucide-react";

import { Dialog, DialogContent, DialogTitle } from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { cn } from "@/shared/lib/utils";
import { useToast } from "@/shared/hooks/use-toast";
import { imageExtFromUrl, triggerDownload } from "@/shared/lib/download";
import { posterService } from "../../api/posterService";
import { buildLightboxSlides } from "./posterLightbox";

interface Props {
  /** 任务 id;null = 关闭态(不拉取、不渲染大图)。 */
  taskId: number | null;
  /** 资产未加载前先展示的封面缩略图,避免打开瞬间空白。 */
  coverUrl?: string | null;
  onClose: () => void;
  /** [进入详情编辑]:交给调用方 navigate 到详情页(职责分离:列表预览、详情编辑)。 */
  onEnterDetail: (taskId: number) => void;
  /**
   * OEM H1:模块未开通 / 停用 / 过期 → 预览与下载照常,「进入详情编辑」(再来一张 / 改图 / 让它动起来所在的编辑面)
   * 换成这句提示。
   */
  lockedNotice?: string;
}

/**
 * 图库列表点缩略图 → 全屏大图 lightbox(不跳页)。
 *
 * - 多图/多版本左右切换(懒加载 /poster/tasks/{id}/assets),**默认展示最新那张**、缩略图胶片条、
 *   版本标注(原图 / 改版 N)+「最新」徽标、键盘 ← → 切换;
 * - 底部 [下载]/[进入详情编辑]。排序 + 版本标注逻辑抽到 {@link buildLightboxSlides}。
 */
export function ImagePreviewLightbox({
  taskId,
  coverUrl,
  onClose,
  onEnterDetail,
  lockedNotice,
}: Props) {
  const { toast } = useToast();
  const open = taskId !== null;
  // null = 用户还没手动切换 → 派生为「最新那张」(slides 末位)。切换后存具体下标。
  // 派生而非 effect 后置 setIdx,避免先渲染原图再跳最新的闪烁。
  const [userIdx, setUserIdx] = useState<number | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [zipping, setZipping] = useState(false);
  // 已加载完成的主图 src:切到未缓存的大图时先显示加载态,避免白屏闪烁。
  const [loadedSrc, setLoadedSrc] = useState<string | undefined>(undefined);
  const stripRef = useRef<HTMLDivElement>(null);

  const assetsQ = useQuery({
    queryKey: ["poster-assets", taskId],
    queryFn: () => posterService.listAssets(taskId as number),
    enabled: open,
  });

  const slides = useMemo(() => buildLightboxSlides(assetsQ.data ?? []), [assetsQ.data]);
  const total = slides.length;
  // 未手动切换 → 最新(total-1);切换过 → 钳到范围内。
  const safeIdx =
    total === 0 ? 0 : userIdx == null ? total - 1 : Math.min(Math.max(userIdx, 0), total - 1);
  const current = total > 0 ? slides[safeIdx] : undefined;
  const currentAsset = current?.asset;
  const src = currentAsset?.baseUrl ?? currentAsset?.thumbUrl ?? coverUrl ?? undefined;
  // 模糊铺底优先用缩略图(胶片条已缓存,瞬显),避免切图时铺底也白屏。
  const bgSrc = currentAsset?.thumbUrl ?? src;
  const imgLoading = src != null && loadedSrc !== src;
  /**
   * 原图到位前铺在主位的缩览(0810 琳琳「点击查看很卡,一直转圈」,与 LongImageReader
   * 的 D-01 同款):长图原图是 1500×9380 级,传输+解码期间只有模糊铺底(读不了内容)+
   * 转圈,用户只能猜是不是卡死了。缩览胶片条已经缓存了同版式的小图 —— 铺上去,
   * 到货原地变清晰、不跳位。src 本身已回落到缩览时没有更小的图可铺,不编一个不存在的。
   */
  const placeholderSrc = [currentAsset?.thumbUrl, coverUrl].find((u) => u && u !== src) ?? null;
  const aw = currentAsset?.actualWidth ?? 0;
  const ah = currentAsset?.actualHeight ?? 0;
  const dims = aw && ah ? `${aw}×${ah}` : null;
  /**
   * ★长图不能按整图 contain(2026-07-29 用户报的):
   * 主舞台限高 68vh,一张 1500×9380(1:6.25)的详情长图 contain 之后宽度只剩 ≈117px ——
   * 屏幕中间一条细线,什么都看不清。这与 W2 D4 实测到的是同一件事(那次「504 高只剩 114px 宽」,
   * 方案 A 因此被否)。详情页当时按宽高比分流去了 `LongImageReader`,而图库/作品库走的是
   * 本组件这条路,一直没跟上。
   * 判据与 `LongImageResultPanel.isTall` 同源(h/w ≥ 1.6),不另立一套。
   */
  const isTall = aw > 0 && ah / aw >= 1.6;

  // 换任务时复位到「跟随最新」。
  useEffect(() => {
    setUserIdx(null);
  }, [taskId]);

  const go = useCallback(
    (delta: number) => {
      if (total <= 1) return;
      setUserIdx((prev) => {
        const cur = prev == null ? total - 1 : Math.min(Math.max(prev, 0), total - 1);
        return (cur + delta + total) % total;
      });
    },
    [total]
  );

  // 键盘 ← → 切换(打开时挂载)。Esc 关闭由 Radix Dialog 自带。
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, go]);

  // 当前项在胶片条里自动滚动进视野。
  useEffect(() => {
    stripRef.current
      ?.querySelector(`[data-slide-idx="${safeIdx}"]`)
      ?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [safeIdx]);

  const download = async () => {
    if (!currentAsset) return;
    setDownloading(true);
    try {
      const url = await posterService.downloadAsset(currentAsset.id);
      triggerDownload(
        url,
        `poster-${currentAsset.taskId}-${currentAsset.seq}.${imageExtFromUrl(url)}`
      );
    } catch (e) {
      toast(errorToast(e, { fallbackTitle: "下载失败" }));
    } finally {
      setDownloading(false);
    }
  };

  // 「下载全部」:把该海报所有版本打成一个 zip(复用列表页多选打包的 batchDownload,传单个 taskId)。
  const downloadAll = async () => {
    if (taskId === null) return;
    setZipping(true);
    try {
      await posterService.batchDownload([taskId]);
      toast({ title: "已开始打包下载" });
    } catch (e) {
      toast(errorToast(e, { fallbackTitle: "打包下载失败" }));
    } finally {
      setZipping(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="max-w-5xl gap-0 overflow-hidden p-0">
        <DialogTitle className="sr-only">图片预览</DialogTitle>

        {/* ── 主舞台 ───────────────────────────────────────────── */}
        {/* ★定位上下文(不滚动)在外层,滚动容器在内层 —— 角标与左右切换按钮必须挂在这一层。
            挂进滚动容器里的话,长图往下滚时它们会跟着内容滚走(W2 D4 同款教训)。 */}
        {/* ★主舞台 flex-1 + min-h-0:高度由弹窗(max-h-[85dvh])减去底部操作条决定,不再用与 85dvh
            各自为政的 68vh。原来 stage 写死 max-h-[68vh]、主图 max-h-[68vh],这些 vh 值不可压缩
            (缺 min-h-0),浏览器缩放(125%/150%,笔记本常见)时把弹窗 max-h 顶破,弹窗比视口高、居中后
            顶底对称溢出,关闭按钮与底部「下载/进入详情」被切出屏幕。测试环境实测:1366×768@125% 长图
            预览顶底各裁 14px(2026-08-12 反馈)。 */}
        <div className="relative flex min-h-0 flex-1 flex-col bg-neutral-950">
          {/* 竖版留白优化:缩略图放大模糊铺底(瞬显),竖版不再是大片黑边,主图仍 contain 居中。 */}
          {bgSrc && (
            <img
              src={bgSrc}
              alt=""
              aria-hidden
              className="absolute inset-0 h-full w-full scale-110 object-cover opacity-25 blur-2xl"
            />
          )}

          <div
            data-testid="lightbox-stage"
            data-tall={isTall || undefined}
            className={cn(
              "relative flex min-h-0 flex-1 justify-center",
              // 长图:按宽铺满 + 容器纵向滚动;其余:整帧居中 contain(存量行为)
              isTall
                ? "items-start overflow-y-auto overflow-x-hidden"
                : "items-center overflow-hidden"
            )}
          >
            {src ? (
              <>
                {/* 原图未到:缩览按主图同一版式占住主位(长图=按宽铺满),到货原地换、不跳位 */}
                {imgLoading && placeholderSrc && (
                  <img
                    src={placeholderSrc}
                    alt=""
                    aria-hidden
                    data-testid="lightbox-placeholder"
                    className={cn(
                      "relative z-10",
                      isTall ? "w-full max-w-full" : "max-h-full max-w-full object-contain"
                    )}
                  />
                )}
                <img
                  key={src}
                  src={src}
                  onLoad={() => setLoadedSrc(src)}
                  alt={`预览 · ${current?.versionLabel ?? ""}`}
                  className={cn(
                    "relative z-10 transition-opacity duration-300",
                    // ★长图按**宽**铺满、高度自然展开(容器负责滚动);限高 contain 会把它压成一条线
                    isTall ? "w-full max-w-full" : "max-h-full max-w-full object-contain",
                    // ★加载期挪出文档流:缩览在场时两个 w-full 的 flex 子元素会各分一半宽,
                    //   缩览被挤成半宽 —— 版式就不是「原地变清晰」了
                    imgLoading ? "absolute opacity-0" : "opacity-100"
                  )}
                />
                {imgLoading && (
                  <div className="absolute top-1/2 z-10 flex -translate-y-1/2 flex-col items-center gap-2">
                    <Loader2 className="h-8 w-8 animate-spin text-white/70" />
                    {placeholderSrc && (
                      <span
                        data-testid="lightbox-loading-note"
                        className="rounded-full bg-black/55 px-2.5 py-1 text-xs text-white/85 backdrop-blur"
                      >
                        原图加载中 · 先看缩览
                      </span>
                    )}
                  </div>
                )}
              </>
            ) : assetsQ.isLoading ? (
              <Loader2 className="relative z-10 h-8 w-8 animate-spin text-white/70" />
            ) : (
              <span className="relative z-10 self-center text-sm text-white/70">
                暂无可预览的图片
              </span>
            )}
          </div>

          {/* 左上角:版本标注 +「最新」+ 尺寸 */}
          {current && (
            <div className="absolute left-3 top-3 z-20 flex flex-wrap items-center gap-2">
              <span
                data-testid="lightbox-version"
                className="rounded-full bg-black/55 px-3 py-1 text-xs font-medium text-white backdrop-blur"
              >
                {current.versionLabel}
              </span>
              {current.isLatest && total > 1 && (
                <span
                  data-testid="lightbox-latest"
                  className="flex items-center gap-1 rounded-full bg-primary/90 px-2.5 py-1 text-xs font-medium text-primary-foreground"
                >
                  <Sparkles className="h-3 w-3" />
                  最新
                </span>
              )}
              {dims && (
                <span className="rounded-full bg-black/55 px-2.5 py-1 text-xs tabular-nums text-white/85 backdrop-blur">
                  {dims}
                </span>
              )}
              {/* 长图取景已换成「按宽铺满 + 可滚」,得说一句 —— 否则用户以为只有这一屏 */}
              {isTall && (
                <span
                  data-testid="lightbox-scroll-hint"
                  className="rounded-full bg-black/55 px-2.5 py-1 text-xs text-white/85 backdrop-blur"
                >
                  长图 · 可上下滚动查看全图
                </span>
              )}
            </div>
          )}

          {/* 左右切换 */}
          {total > 1 && (
            <>
              <button
                type="button"
                aria-label="上一张"
                onClick={() => go(-1)}
                className="absolute left-3 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur transition hover:bg-black/70"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                aria-label="下一张"
                onClick={() => go(1)}
                className="absolute right-3 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur transition hover:bg-black/70"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}
        </div>

        {/* ── 底部:缩略图胶片条 + 操作 ─────────────────────────── */}
        <div className="space-y-3 border-t bg-background p-3">
          {total > 1 && (
            <div ref={stripRef} className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:thin]">
              {slides.map((s, i) => (
                <button
                  key={s.asset.id}
                  type="button"
                  data-slide-idx={i}
                  aria-label={`查看${s.versionLabel}`}
                  aria-current={i === safeIdx}
                  onClick={() => setUserIdx(i)}
                  className={cn(
                    "relative h-16 w-16 shrink-0 overflow-hidden rounded-md border-2 transition",
                    i === safeIdx
                      ? "border-primary ring-2 ring-primary/30"
                      : "border-transparent opacity-60 hover:opacity-100"
                  )}
                >
                  <img
                    src={s.asset.thumbUrl ?? s.asset.baseUrl ?? ""}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                  {s.isLatest && (
                    <span className="absolute inset-x-0 bottom-0 bg-primary/85 py-0.5 text-center text-2xs font-medium leading-none text-primary-foreground">
                      最新
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between gap-2">
            <span className="truncate text-xs text-muted-foreground">
              {current ? current.versionLabel : "—"}
              {dims ? ` · ${dims}` : ""}
              {total > 1 ? ` · ${safeIdx + 1}/${total}` : ""}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!currentAsset || downloading}
                onClick={download}
              >
                {downloading ? (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-1 h-4 w-4" />
                )}
                下载
              </Button>
              {total > 1 && (
                <Button variant="outline" size="sm" disabled={zipping} onClick={downloadAll}>
                  {zipping ? (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  ) : (
                    <Package className="mr-1 h-4 w-4" />
                  )}
                  下载全部
                </Button>
              )}
              {lockedNotice ? (
                <Button
                  size="sm"
                  disabled
                  title={lockedNotice}
                  data-testid="module-locked-notice"
                  aria-label={`进入详情编辑(${lockedNotice})`}
                >
                  <Pencil className="mr-1 h-4 w-4" />
                  进入详情编辑
                </Button>
              ) : (
                <Button
                  size="sm"
                  disabled={taskId === null}
                  onClick={() => taskId !== null && onEnterDetail(taskId)}
                >
                  <Pencil className="mr-1 h-4 w-4" />
                  进入详情编辑
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
